import { SpokeSpinWrapper } from '@doltech/core/lib/auth/SpokeSpinWrapper';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { IconOtherPause } from '@doltech/icons/IconOtherPause';
import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../../colors-v2';
import { Typography } from '../../DesignSystemV2/Typography';
import {
  useUncontrolledAudioPlayerStore,
  withUncontrolledAudioPlayerStore,
} from './uncontrolled-audio-player.store';
import { DurationComponent, UncontrolledAudioPlayer } from './UncontrolledAudioPlayer';

const Main = styled.div`
  display: grid;
  gap: 12px;
  grid-auto-flow: column;
  grid-template-columns: minmax(0, max-content) 1fr;
  align-items: center;
  border: 1px solid ${colorsV2.gray40};
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  background-color: ${colorsV2.white100};
  padding: 8px 18px 8px 8px;

  .dol-audio-player,
  .preview-audio-player {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 1fr minmax(0, max-content);
    align-items: center;
    gap: 12px;
  }

  .btn-open-preview {
    font-size: 16px;
    line-height: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
  }

  .button-audio-play {
    transition: all 0.3s ease;
    cursor: pointer;
    box-shadow: 0 7px 14px rgba(45, 55, 66, 0.22);
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background-color: ${colorsV2.primary100};
    color: ${colorsV2.white100};

    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
  }

  .hide {
    display: none;
  }

  &.dark {
    background-color: #25426b;
    .dol-audio-player,
    .preview-audio-player {
      color: ${colorsV2.white100};
    }
    .duration-component {
      span {
        color: ${colorsV2.white100} !important;
      }
    }
  }
`;

interface ToggleAudioPlayerProps {
  titlePreview: string;
  url: string;
  darkMode?: boolean;
  isLoading?: boolean;
}

const ToggleAudioPlayerBase = (props: ToggleAudioPlayerProps) => {
  const { url, darkMode, titlePreview, isLoading = false } = props;
  const store = useUncontrolledAudioPlayerStore();
  const [isPreview, setIsPreview] = React.useState(true);
  return (
    <SpokeSpinWrapper spinning={isLoading}>
      <Main
        onClick={(e) => {
          if (isPreview) {
            e.preventDefault();
            e.stopPropagation();
            setIsPreview(false);
            store.playPause();
          }
        }}
        className={cl('toggle-audio-player', { dark: darkMode })}
      >
        <div
          aria-hidden
          onClick={() => {
            if (isPreview) {
              return;
            }
            store.playPause();
          }}
          className="button-audio-play"
        >
          {store.isPlaying ? <IconOtherPause /> : <IconOtherPlay />}
        </div>
        <div className={cl('preview-audio-player', { hide: !isPreview })}>
          <Typography.Paragraph variant="semi-bold/14">{titlePreview}</Typography.Paragraph>
          <DurationComponent durationInSeconds={store.durationInSeconds} />
        </div>
        <div className={cl('dol-audio-player', { hide: isPreview })}>
          <UncontrolledAudioPlayer url={url} volume={1} interactive />
          <div
            aria-hidden
            onClick={() => {
              setIsPreview(true);
              store.setPlaying(false);
              store.setSlideProgressInSeconds(store.progressInSeconds);
            }}
            className="btn-open-preview"
          >
            <IconOtherClose />
          </div>
        </div>
      </Main>
    </SpokeSpinWrapper>
  );
};

export const ToggleAudioPlayer = compose(
  withUncontrolledAudioPlayerStore,
  observer
)(ToggleAudioPlayerBase);
